/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 * @description SpotlightListingCardContent
 */

import buttonCss from '@zcode-ui/web-zumper-features/src/ListingCard/buttons.module.scss'
import { VerifiedIcon } from '@zcode-ui/web-zumper-ui'
import type { Listable } from '@zcode/state-type'
import cn from 'classnames'
import React, { memo, type MouseEvent } from 'react'
import { DetailPageLink } from '../../../../detail/DetailPageLink/DetailPageLink'
import { useGetCommonEntityData } from '../ListingCard/helpers/useGetCommonEntityData'
import css from './SpotlightListingCardContent.module.scss'

export interface SpotlightListingCardContentProps {
  entity: Listable
  handleQuickLookClick: (e: MouseEvent<HTMLButtonElement>) => void
  hideQuickLook?: boolean
  isLargeSpotlight?: boolean
}

export const SpotlightListingCardContent = memo(
  ({
    entity,
    handleQuickLookClick,
    hideQuickLook,
    isLargeSpotlight,
  }: SpotlightListingCardContentProps) => {
    const getCommonEntityData = useGetCommonEntityData()
    const { headerText, isBuilding, isVerified } = entity
    const { fullAddress, price } = getCommonEntityData({ entity })

    return (
      <div className={cn(css.contentContainer, isLargeSpotlight && css.contentContainerLargeCard)}>
        <div className={cn(css.headerContainer, isLargeSpotlight && css.headerContainerLargeCard)}>
          <DetailPageLink
            className={cn(
              'font-bold font-heading hover:no-underline hover:text-gray-0 text-gray-0',
              !isLargeSpotlight && 'leading-[22px] text-[18px] truncate',
              isLargeSpotlight && 'leading-[120%] text-xl pb-0.5 whitespace-normal'
            )}
            entity={entity}
          >
            {isBuilding ? headerText : fullAddress}
          </DetailPageLink>
          {isVerified && (
            <VerifiedIcon
              popoverId={`verified-${entity.uId}`}
              isDirectDeal
              isNewDetailPage
              isWhite
              size={18}
            />
          )}
        </div>
        <div
          className={cn(
            css.addressAndPriceText,
            isLargeSpotlight && css.addressAndPriceTextLargeCard
          )}
        >
          {fullAddress}
        </div>
        <div className={css.priceContainer}>
          <div
            className={cn(
              css.addressAndPriceText,
              isLargeSpotlight && css.addressAndPriceTextLargeCard
            )}
          >
            {price}
          </div>
          {!hideQuickLook && !isLargeSpotlight && (
            <>
              <span className={css.divider}>|</span>
              <button
                className={cn(buttonCss.baseButton, css.quickLookBtn)}
                onClick={handleQuickLookClick}
                type="button"
              >
                Quick look
              </button>
            </>
          )}
        </div>
      </div>
    )
  }
)
